import { useState, useEffect } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";

/** Format a decimal inches value to a mixed-number string (36.5 → 36 1/2"). */
function fmtInches(v) {
  if (v === "" || v == null || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  const total16 = Math.round(n * 16);
  const whole = Math.floor(total16 / 16);
  const sx = total16 % 16;
  if (sx === 0) return `${whole}"`;
  const g = (a, b) => (b ? g(b, a % b) : a);
  const d = g(sx, 16);
  return `${whole > 0 ? whole + " " : ""}${sx / d}/${16 / d}"`;
}

/**
 * Final-confirmation modal shown when the customer taps "Submit measurements".
 * Forces them to read a no-warranty-on-mismeasurement disclosure and tick a
 * confirmation checkbox before the order moves to production.
 */
export default function MeasurementConfirmModal({
  open, onClose, onConfirm, items = [], productName = "screen", saving = false,
}) {
  const [acknowledged, setAcknowledged] = useState(false);

  // Reset the checkbox each time the modal re-opens so the customer
  // has to read & confirm afresh on every submit attempt.
  useEffect(() => {
    if (open) setAcknowledged(false);
  }, [open]);

  if (!open) return null;

  const handleConfirm = () => {
    if (!acknowledged || saving) return;
    onConfirm();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4 py-6"
      style={{ background: "rgba(15, 23, 42, 0.55)" }}
      data-testid="measure-confirm-modal"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-md rounded-2xl shadow-2xl"
        style={{ background: "#fff" }}
      >
        <div className="px-5 pt-5 pb-3 border-b border-[var(--brand-border)]">
          <div className="flex items-start gap-3">
            <div
              className="rounded-full p-2 shrink-0"
              style={{ background: "#fef3c7" }}
            >
              <AlertTriangle className="w-5 h-5" style={{ color: "#92400e" }} />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-extrabold text-[var(--brand-text)]">
                Final check before we build
              </h2>
              <p className="text-xs muted-text mt-1">
                Once you submit, production starts using these exact measurements.
              </p>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 max-h-[55vh] overflow-y-auto">
          <p className="text-sm font-semibold text-[var(--brand-text)] mb-2">
            You&apos;re ordering:
          </p>
          <ul className="rounded-xl divide-y divide-[var(--brand-border)] border border-[var(--brand-border)] mb-4">
            {items.map((it, i) => (
              <li
                key={i}
                className="flex items-center justify-between px-3 py-2 text-sm"
                data-testid={`confirm-item-${i}`}
              >
                <span className="font-semibold text-[var(--brand-text)]">
                  Item {i + 1}
                  <span className="font-normal muted-text ml-2">
                    {it.frame_color || "—"}
                  </span>
                </span>
                <span className="font-mono text-[var(--brand-text)] font-semibold">
                  {fmtInches(it.width)} × {fmtInches(it.height)}
                </span>
              </li>
            ))}
          </ul>

          <div
            className="rounded-xl p-3 text-xs leading-relaxed"
            style={{ background: "#fef3c7", color: "#92400e" }}
          >
            <p className="font-bold mb-1.5">Please read carefully:</p>
            <p>
              Each {productName.toLowerCase()} is custom-built to the dimensions you provide.
              Once production begins, we <strong>cannot resize, recut, or remake</strong> a
              screen that was built to incorrect measurements free of charge — a new order
              and full repurchase would be required.
            </p>
            <p className="mt-2">
              Please double-check every width and height before continuing.
            </p>
          </div>

          <label className="flex items-start gap-2 mt-4 cursor-pointer text-sm">
            <input
              type="checkbox"
              data-testid="confirm-acknowledge"
              className="mt-0.5"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
            <span className="text-[var(--brand-text)]">
              I&apos;ve double-checked and confirm these measurements are correct.
              I understand my screens will be built to these exact dimensions.
            </span>
          </label>
        </div>

        <div className="px-5 py-4 border-t border-[var(--brand-border)] flex flex-col-reverse sm:flex-row gap-2">
          <button
            type="button"
            data-testid="confirm-cancel-btn"
            onClick={onClose}
            disabled={saving}
            className="flex-1 py-3 rounded-full border-2 border-[var(--brand-border)] font-semibold text-[var(--brand-text)] hover:border-[var(--brand-green)] disabled:opacity-60"
          >
            Go back &amp; double-check
          </button>
          <button
            type="button"
            data-testid="confirm-submit-btn"
            onClick={handleConfirm}
            disabled={!acknowledged || saving}
            className="cta-btn flex-1 disabled:opacity-50"
          >
            {saving ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
              </span>
            ) : (
              "Submit final measurements"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
